import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Menu, Clock, Calendar, Receipt, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import MenuBar from "@/components/MenuBar";
import { cn } from "@/lib/utils";

import {
  ApiResponse,
  SubmissionResponse,
} from "@/components/submission/types";
import { Expense, HoursWorked, TimeOffRequest } from "@/types/nanny";

const EmployerSubmissionView: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const { toast } = useToast();

  const [submission, setSubmission] = useState<SubmissionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!submissionId) return;

      try {
        setIsLoading(true);

        const submissionData = await ApiService.request<ApiResponse<SubmissionResponse>>(
          EndpointKeys.GET_SUBMISSION,
          {
            urlParams: { id: Number(submissionId) },
          },
        );

        setSubmission(submissionData.data);
      } catch (err) {
        console.log(err);
        setError(err instanceof Error ? err.message : "An error occurred");
        toast({
          title: "Error",
          description: "Failed to load submission data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmission();
  }, [submissionId, toast]);

  const handleApprove = async () => {
    if (!submissionId) return;

    try {
      setIsApproving(true);
      await ApiService.request(EndpointKeys.APPROVE_SUBMISSION, {
        method: "POST",
        urlParams: { submissionId: Number(submissionId) },
      });

      toast({
        title: "Success",
        description: "Submission approved successfully",
      });

      // Refresh the submission data
      const submissionData = await ApiService.request<ApiResponse<SubmissionResponse>>(
        EndpointKeys.GET_SUBMISSION,
        {
          urlParams: { id: Number(submissionId) },
        },
      );
      setSubmission(submissionData.data);
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to approve submission",
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!submissionId) return;

    try {
      setIsRejecting(true);
      await ApiService.request(EndpointKeys.REJECT_SUBMISSION, {
        method: "POST",
        urlParams: { submissionId: Number(submissionId) },
        body: { reason },
      });

      toast({
        title: "Success",
        description: "Submission rejected successfully",
      });

      // Refresh the submission data
      const submissionData = await ApiService.request<ApiResponse<SubmissionResponse>>(
        EndpointKeys.GET_SUBMISSION,
        {
          urlParams: { id: Number(submissionId) },
        },
      );
      setSubmission(submissionData.data);
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to reject submission",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      DRAFT: { variant: "secondary" as const, label: "Draft", className: "" },
      SUBMITTED: { variant: "default" as const, label: "Submitted", className: "" },
      APPROVED: { variant: "default" as const, label: "Approved", className: "bg-green-100 text-green-800" },
      REJECTED: { variant: "destructive" as const, label: "Rejected", className: "" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
    
    return (
      <Badge 
        variant={config.variant} 
        className={config.className}
      >
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!submission) {
    return <div>No submission found</div>;
  }

  const submissionMonth = format(
    parseISO(submission.submission_month),
    "MMMM yyyy",
  );

  const canApprove = submission.status === "SUBMITTED";
  const isRejected = submission.status === "REJECTED";

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 overflow-auto">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl">Monthly Submission Review</h1>
              <p className="text-muted-foreground">{submissionMonth}</p>
            </div>
            <div className="flex items-center gap-4">
              {getStatusBadge(submission.status)}
              {canApprove && (
                <div className="flex gap-2">
                  <Button 
                    onClick={handleApprove}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={isApproving || isRejecting}
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      "Approve"
                    )}
                  </Button>
                  <Button 
                    variant="destructive"
                    disabled={isApproving || isRejecting}
                    onClick={() => {
                      const reason = prompt("Please provide a reason for rejection:");
                      if (reason) {
                        handleReject(reason);
                      }
                    }}
                  >
                    {isRejecting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Rejecting...
                      </>
                    ) : (
                      "Reject"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {isRejected && (
            <Alert className="mb-6" variant="destructive">
              <AlertTitle>Submission Rejected</AlertTitle>
              <AlertDescription>
                This submission has been rejected and sent back to your nanny for revision.
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="hours" className="w-full">
            <TabsList className="w-full justify-start overflow-auto">
              <TabsTrigger value="hours">
                <Clock className="w-4 h-4 mr-2" />
                Hours Worked
              </TabsTrigger>
              <TabsTrigger value="timeoff">
                <Calendar className="w-4 h-4 mr-2" />
                Time Off
              </TabsTrigger>
              <TabsTrigger value="expenses">
                <Receipt className="w-4 h-4 mr-2" />
                Expenses
              </TabsTrigger>
            </TabsList>

            <TabsContent value="hours">
              <Card>
                <CardHeader>
                  <CardTitle>Hours Worked</CardTitle>
                  <CardDescription>
                    Review the hours worked by your nanny
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submission.hours_worked.filter(hours => hours.status !== "DELETED").length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {submission.hours_worked
                          .filter(hours => hours.status !== "DELETED")
                          .map((hours: HoursWorked) => (
                          <TableRow key={hours.id}>
                            <TableCell>
                              {format(new Date(hours.work_date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>{hours.earning_name}</TableCell>
                            <TableCell>{hours.hours} hours</TableCell>
                            <TableCell>
                              <Badge 
                                variant={hours.status === "APPROVED" ? "default" : "secondary"}
                                className={hours.status === "APPROVED" ? "bg-green-100 text-green-800" : ""}
                              >
                                {hours.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground">No hours recorded for this period.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timeoff">
              <Card>
                <CardHeader>
                  <CardTitle>Time Off Requests</CardTitle>
                  <CardDescription>
                    Review time off requests from your nanny
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submission.time_off_requests.filter(timeOff => timeOff.status !== "DELETED").length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Start Date</TableHead>
                          <TableHead>End Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {submission.time_off_requests
                          .filter(timeOff => timeOff.status !== "DELETED")
                          .map((timeOff: TimeOffRequest) => (
                          <TableRow key={timeOff.id}>
                            <TableCell>
                              {format(new Date(timeOff.start_date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>
                              {format(new Date(timeOff.end_date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>{timeOff.leave_type_name}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={timeOff.status === "APPROVED" ? "default" : "secondary"}
                                className={timeOff.status === "APPROVED" ? "bg-green-100 text-green-800" : ""}
                              >
                                {timeOff.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground">No time off requests for this period.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses">
              <Card>
                <CardHeader>
                  <CardTitle>Expenses</CardTitle>
                  <CardDescription>
                    Review expense claims from your nanny
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submission.expenses.filter(expense => expense.status !== "DELETED").length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Receipt</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {submission.expenses
                          .filter(expense => expense.status !== "DELETED")
                          .map((expense: Expense) => (
                          <TableRow key={expense.id}>
                            <TableCell>{expense.description}</TableCell>
                            <TableCell>{formatCurrency(expense.amount)}</TableCell>
                            <TableCell>
                              {expense.receipt_url ? (
                                <a 
                                  href={expense.receipt_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  View Receipt
                                </a>
                              ) : (
                                "No receipt"
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={expense.status === "APPROVED" ? "default" : "secondary"}
                                className={expense.status === "APPROVED" ? "bg-green-100 text-green-800" : ""}
                              >
                                {expense.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground">No expenses claimed for this period.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EmployerSubmissionView; 